/**
 * LP Drawer — slide-in side panel controller.
 *
 * Auto-inits every `[data-lp-drawer]` element on the page and exposes
 * `drawer.__lp = { open, close, setView }` for external callers (e.g. the
 * AJAX add-to-cart flow flips the view to "cart" and calls open() after
 * a successful POST to /cart/add.js).
 *
 * Openers are matched by `aria-controls` pointing at the drawer's id, so
 * any button anywhere on the page can open a specific drawer just by:
 *   <button data-lp-drawer-open aria-controls="lp-men-drawer">…</button>
 *
 * Closers live inside the drawer markup with `data-lp-drawer-close`.
 *
 * Re-fires init on `shopify:section:load` so the drawer keeps working when
 * the theme editor swaps a section live.
 */
(function () {
  'use strict';

  function initDrawer(drawer) {
    if (drawer.__lpDrawerInited) return;
    drawer.__lpDrawerInited = true;

    var id = drawer.id;
    // Openers can be anywhere on the page — match by aria-controls so a
    // sticky header button outside the section can still open the drawer.
    var openers = id
      ? document.querySelectorAll('[data-lp-drawer-open][aria-controls="' + id + '"]')
      : document.querySelectorAll('[data-lp-drawer-open]');
    var closers = drawer.querySelectorAll('[data-lp-drawer-close]');

    function setView(view) {
      drawer.setAttribute('data-view', view);
    }

    function open(opts) {
      opts = opts || {};
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      document.body.style.overflow = 'hidden';
      if (opts.skipFocus) return;
      // The cart-success view is informational — focus the close button so
      // Esc/Tab still work but we don't move focus to a CTA the user
      // didn't ask to engage with.
      var view = drawer.getAttribute('data-view') || 'nav';
      var focusTarget = view === 'cart'
        ? drawer.querySelector('.lp-drawer__close')
        : drawer.querySelector('.lp-drawer__link, .lp-drawer__close');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      document.body.style.overflow = '';
    }

    drawer.__lp = { open: open, close: close, setView: setView };

    openers.forEach(function (b) {
      b.addEventListener('click', function () {
        // Hamburger always means "show the menu" — reset view first so a
        // prior cart-success view doesn't surprise the user.
        setView('nav');
        open();
      });
    });
    closers.forEach(function (b) { b.addEventListener('click', close); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-lp-drawer]').forEach(initDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });
})();
